<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
import {
  orderBy, uniqBy, isEmpty, filter,
} from 'lodash';
import markersInDirectory from 'api/markersInDirectory';
import { C_ANSWER_TEMPLATE_INTERPOLATION_CODES } from 'src/store/modules/app';
import {
  toast,
  getDirectoriesFromMarkers,
  parseAnswerTemplateForReplacement,
} from 'src/utils';

function getMarkersFromTemplates(acc, elem) {
  const joinArrs = [...acc, ...elem.markers];
  return uniqBy(joinArrs, 'id');
}

export default {
  name: 'answerTemplatesWithMarkers',
  props: ['value'],
  data() {
    return {
      requestTypeId: null,
      errorNotification: false,
      activeMarker: null,
      activeDirectory: null,
      markers: [],
      loadingFormData: true,
      loadingMarks: false,
      templateSearchText: '',
      sentForSearchText: '',
      searchInputDisabled: false,
      isSearchMode: false,
      selectedTemplate: null,
      selectedTemplateActiveDirectory: null,
      selectedTemplateMarkers: [],
      serviceDirectory: {
        id: 'serviceDirectory',
        markers: [],
        name: this.$t('T_NO_THEME'),
      },
      isSearchLoading: false,
      showActiveDirectoryTooltip: false,
      hasTemplatesFlag: false,
      hasMarkerTemplatesFlag: false,
    };
  },
  async mounted() {
    await this.loadAnswerTemplates();
    this.init();
    this.updateHasTemplatesFlag(this.sortedDirectories);
    this.updateHasMarkerTemplatesFlag(this.markers);
  },
  watch: {
    templateSearchText(val) {
      if (!val && this.isSearchMode) {
        this.CLEAR_ANSWER_TEMPLATES_LOADED();
        this.emptyData();
        this.isSearchMode = false;
        if (this.sortedDirectories.length) {
          this.onshowMarks(this.sortedDirectories[0]);
        }
      }
    },
    sortedDirectories(newDirectories) {
      this.updateHasTemplatesFlag(newDirectories);
    },
    markers(newMarkers) {
      this.updateHasMarkerTemplatesFlag(newMarkers);
    },
  },
  computed: {
    ...mapState('app', ['mode', 'isFrame']),
    ...mapState('operatorChat', ['answerTemplates', 'foundAnswerTemplates']),
    ...mapState('interest', ['directories']),
    ...mapState('home', ['ucceIntegration']),
    ...mapGetters('activities', ['getActivityById']),
    ...mapGetters('user', ['isExternalOperator']),
    ucceEnabled() {
      return this.ucceIntegration;
    },
    currentActivity() {
      return this.getActivityById(this.value.activityId);
    },
    sortedDirectories() { // темы, отсортированные по алфавиту (шаблон не выделен)
      let isFoundTemplateWithoutMarkers;
      if (!this.isSearchMode) { // не в режиме поиска формируем темы на основе подгруженных тем
        const orderedDirectories = orderBy(this.directories, [(i) => i.name.toLowerCase()]);
        isFoundTemplateWithoutMarkers = this.answerTemplates
          .some((template) => isEmpty(template.markers));
        if (isFoundTemplateWithoutMarkers) {
          // если есть шаблоны без маркеров, добавляем тему "без темы"
          return [this.serviceDirectory, ...orderedDirectories];
        }
        return orderedDirectories;
      }
      // в режиме поиска темы формируем на основе найденных шаблонов
      const foundTemplatesMarkers = this.foundAnswerTemplates.reduce(getMarkersFromTemplates, []);
      const foundTemplatesDirectories = foundTemplatesMarkers.reduce(getDirectoriesFromMarkers, []);
      const orderedfoundTemplatesDirectories = orderBy(foundTemplatesDirectories, [(i) => i.name.toLowerCase()]);
      isFoundTemplateWithoutMarkers = this.foundAnswerTemplates
        .some((template) => isEmpty(template.markers));
      if (isFoundTemplateWithoutMarkers) {
        // если есть шаблоны без маркеров, добавляем тему "без темы"
        return [this.serviceDirectory, ...orderedfoundTemplatesDirectories];
      }
      return orderedfoundTemplatesDirectories;
    },
    selectedTemplateDirectories() { // темы, отсортированные по алфавиту, для выделенного шаблона
      if (isEmpty(this.selectedTemplate.markers)) {
        return [this.serviceDirectory];
      }
      const selectedTemplateDirectories = this.selectedTemplate.markers.reduce(getDirectoriesFromMarkers, []);
      return orderBy(selectedTemplateDirectories, [(i) => i.name.toLowerCase()]);
    },
    selectedTemplateMarkersByDirectory() { // маркеры, отсортированные по алфавиту
      // для выделенного шаблона (выбрана тема или нет)
      return !this.selectedTemplateActiveDirectory ? [] : this.selectedTemplate.markers
        .filter((marker) => marker.directory.id === this.selectedTemplateActiveDirectory.id);
    },
    templatesWithoutMarkers() { // шаблоны без маркеров (не в режиме поиска)
      return this.answerTemplates.filter((template) => isEmpty(template.markers));
    },
    templatesInView() {
      // в режиме поиска
      if (this.isSearchMode) {
        return this.templatesInViewInSearchMode;
      }
      return this.templatesInViewInRegularMode;
    },
    templatesInViewInRegularMode() {
      if (this.activeDirectory?.id === 'serviceDirectory') return this.templatesWithoutMarkers;
      let resTemplates = [];
      const currentMarker = this.markers.find((marker) => marker.id === this.activeMarker?.id);
      if (currentMarker) { // если юзер нажал маркер,
        // отображаем соответствующие этому маркеру шаблоны
        resTemplates = currentMarker.answerTemplates;
        if (!isEmpty(resTemplates)) {
          resTemplates = uniqBy(resTemplates, 'id');
          resTemplates = orderBy(resTemplates, [(i) => i.text.toLowerCase()]);
        }
      } else {
        // если нет нажатых маркеров
        // отображаем все доступные шаблоны для текущих маркеров текущего справочника
        const markersWithTemplates = filter(this.markers, (i) => i.answerTemplates.length > 0);
        if (markersWithTemplates.length) {
          markersWithTemplates.forEach((m) => {
            resTemplates = uniqBy(resTemplates.concat(m.answerTemplates), 'id');
            resTemplates = orderBy(resTemplates, [(i) => i.text.toLowerCase()]);
          });
        }
      }
      return resTemplates;
    },
    templatesInViewInSearchMode() {
      if (!this.activeDirectory) {
        return this.foundAnswerTemplates;
      }
      if (this.activeDirectory?.id === 'serviceDirectory') {
        return this.foundAnswerTemplates.filter((template) => isEmpty(template.markers));
      }
      if (this.activeMarker) {
        return this.foundAnswerTemplates
          .filter((template) => template.markers.some((marker) => marker.id === this.activeMarker.id));
      } return this.foundAnswerTemplates
        .filter((template) => template.markers.some((marker) => this.activeDirectory.markers
          .some((activeDirectoryMarker) => activeDirectoryMarker.id === marker.id)));
    },
    searchDisabled() {
      return (isEmpty(this.sortedDirectories) && !this.isSearchMode)
        || this.value.isClosed;
    },
    handleNoTemplatesText() {
      return this.isSearchMode
        ? `<span>${this.$t('T_NO_AVAILABLE_TEMPLATES_FOR_REQUEST')}: <span style="font-weight: bold">${this.sentForSearchText}</span></span>`
        : this.$t('T_NO_AVAILABLE_TEMPLATES');
    },
    miniMode() {
      return this.mode === 'MINI' && this.isFrame && !this.ucceEnabled;
    },
    buttonSize() {
      return this.miniMode ? 'middle' : 'large';
    },
    handleMarkersForDirectoryMini() {
      return this.activeDirectory
        ? `${this.$t('T_MARKERS_FOR_THEMES')}: ${this.activeDirectory.name}`
        : this.$t('T_MARKERS_FOR_THEMES');
    },
  },
  methods: {
    ...mapMutations('operatorChat', ['CLEAR_ANSWER_TEMPLATES_LOADED']),
    ...mapActions('operatorChat', ['loadAnswerTemplates', 'searchAnswerTemplates']),
    ...mapActions('interest', ['fetchDirectories']),
    init() {
      this.fetchDirectories().then(() => {
        if (this.sortedDirectories.length) {
          this.onshowMarks(this.sortedDirectories[0]);
          return;
        } this.loadingFormData = false;
      });
    },
    emptyData() {
      this.selectedTemplate = null;
      this.activeDirectory = null;
      this.activeMarker = null;
      this.selectedTemplateActiveDirectory = null;
      this.markers = [];
      this.selectedTemplateMarkers = [];
    },
    disabledMarker(id) {
      if (!this.selectedTemplate) {
        return this.activeMarker?.id === id;
      } return false;
    },
    onshowMarks(directory) {
      this.loadingMarks = true;
      const lastDirectory = this.activeDirectory;
      this.activeDirectory = directory;
      if (directory.id !== 'serviceDirectory') {
        if (!this.isSearchMode) {
          markersInDirectory(directory.id).then((markers) => {
            if (this.activeDirectory === directory) {
              this.markers = orderBy(markers, [(m) => m.name.toLowerCase()]);
            }
          }).catch(() => {
            this.activeDirectory = lastDirectory;
          })
            .finally(() => {
              this.emptyMarkersData();
            });
        } else {
          this.markers = this.activeDirectory.markers;
          this.emptyMarkersData();
        }
      } else {
        this.markers = [];
        this.emptyMarkersData();
      }
    },
    emptyMarkersData() {
      this.loadingMarks = false;
      this.activeMarker = null;
      this.loadingFormData = false;
    },
    onClickDirectoryBtn(event, directory) {
      event.stopPropagation();
      this.onshowMarks(directory);
    },
    onClickDirectoryBtnWrap(directory) {
      if (this.isSearchMode && !this.selectedTemplate && directory.id === this.activeDirectory?.id) {
        this.activeDirectory = null;
        this.activeMarker = null;
        this.markers = [];
      }
    },
    onClickSelectedTemplateDirectoryBtn(directory) {
      this.selectedTemplateActiveDirectory = directory;
    },
    setActiveMarker(marker) {
      if (!this.selectedTemplate) {
        this.activeMarker = marker;
      }
    },
    setTemplate(event, template) {
      event.stopPropagation();
      const parsedTemplate = parseAnswerTemplateForReplacement({
        template,
        customer: this.currentActivity.customer,
        isExternalOperator: this.isExternalOperator,
        interpolationCodes: this.$store.state.app[C_ANSWER_TEMPLATE_INTERPOLATION_CODES],
      });
      this.value.onGeneterateTemplate(parsedTemplate);
      this.close();
    },
    close() {
      this.$parent.close([null]);
    },
    onSearchTemplates() {
      if (this.templateSearchText) {
        this.isSearchLoading = true;
        this.searchAnswerTemplates(this.templateSearchText)
          .then(() => {
            this.sentForSearchText = this.templateSearchText;
            this.isSearchLoading = false;
            this.isSearchMode = true;
            this.emptyData();
          })
          .catch(() => {
            toast.error({
              msg: this.$t('T_ANSWER_TEMPLATES_SEARCH_ERROR'),
            });
            this.sentForSearchText = '';
            this.isSearchLoading = false;
          });
      }
    },
    handleDirectoryColor(directory) {
      if (directory.id === 'serviceDirectory') {
        return 'orange';
      } return 'socialVk';
    },
    clickTemplate(template) {
      if (this.isSearchMode) {
        if (this.selectedTemplate?.id !== template.id) {
          this.selectedTemplate = template;
          if (this.selectedTemplateDirectories.length) {
            [this.selectedTemplateActiveDirectory] = this.selectedTemplateDirectories;
          }
        } else {
          this.selectedTemplate = null;
        }
      }
    },
    isSelectedTemplate(template) {
      return template.id === this.selectedTemplate?.id;
    },
    handleTemplateVal(templateStr) {
      if (!this.isSearchMode) {
        return templateStr;
      } return this.getHighlightedText(templateStr, this.sentForSearchText);
    },
    getHighlightedText(text, higlight) {
      const groups = higlight.split(' ').map((v) => `(${v})`).join('|');
      const replaced = text.replace(new RegExp(groups, 'gi'), (g) => `<span style="background:#ffef04c9">${g}</span>`);
      return `<span>${replaced}<span>`;
    },
    mouseOver() {
      const ref = this.$refs.activeDirectory;
      this.showActiveDirectoryTooltip = ref?.scrollWidth > ref?.clientWidth;
    },
    hasAnswerTemplates(directoryId) {
      if (this.isSearchMode) {
        return true;
      }
      if (directoryId === 'serviceDirectory') {
        return this.answerTemplates.some((template) => isEmpty(template.markers));
      }
      const directory = this.sortedDirectories.find((t) => t.id === directoryId);
      if (!directory || !directory.markers) {
        return false;
      }
      return directory.markers.some((marker) => marker.answerTemplates && marker.answerTemplates.length > 0);
    },
    hasMarkerTemplates(markerId) {
      if (this.isSearchMode) {
        return true;
      }
      const marker = this.markers.find((m) => m.id === markerId);
      if (!marker || !marker.answerTemplates) {
        return false;
      }
      return marker.answerTemplates.length > 0;
    },
    updateHasTemplatesFlag(directories) {
      if (this.isSearchMode) {
        this.hasTemplatesFlag = true;
      } else {
        this.hasTemplatesFlag = directories.some((directory) => directory.id === 'serviceDirectory');

        if (!this.hasTemplatesFlag) {
          this.hasTemplatesFlag = directories.some((directory) => directory.markers && directory.markers.some((marker) => marker.answerTemplates && marker.answerTemplates.length > 0));
        }
      }
    },
    updateHasMarkerTemplatesFlag(markers) {
      if (this.isSearchMode) {
        this.hasMarkerTemplatesFlag = true;
      } else {
        this.hasMarkerTemplatesFlag = markers.some((marker) => marker.answerTemplates && marker.answerTemplates.length > 0);
      }
    },
  },
};
</script>

<template lang="pug">
div
  div(:class="[$style.form, miniMode && $style.formMini]", v-if="!loadingFormData")
    header(:class="$style.formHeader")
      div(:class="[$style.title, miniMode && $style.titleMini]" data-test="TEMPLATE_LIST") {{ $t('T_ADD_ANSWER_TEMPLATE') }}
      div(:class="$style.icon", @click="close",
          tabindex="0", @keyup.enter="close")
        ui-icon(:class="$style.close", key="close", :title="$t('T_CLOSE')", data-test="CLOSE_BUTTON") close
    div(:class="$style.formBody")
      div(:class="[$style.table, isSearchMode && $style.tableSearch]")
        div(v-if="!miniMode")
          div(v-if="isSearchMode" :class="$style.searchModeIndicatorWrap")
            div(:class="$style.searchModeIndicator") {{ $t('T_SEARCH_MODE') }}
          div(:class="$style.formBodyHeaders")
            div(:class="$style.columnHeader")
                div(:class="$style.subTitle") {{ `${$t('T_ANSWER_TEMPLATE_THEMES')}:` }}
            div(:class="$style.columnHeader")
              div(:class="$style.markersColumnHeader") {{ `${$t('T_MARKERS_FOR_THEMES')}:` }}
              v-tooltip(right content-class="vuetifyTooltip" max-width="300px" v-if="activeDirectory && showActiveDirectoryTooltip" z-index="10000")
                template(v-slot:activator="{ on, attrs }")
                  span(v-on="on" :class="$style.markerChosen" @mouseover="mouseOver" ref="activeDirectory") {{ activeDirectory.name }}
                span {{ activeDirectory.name }}
              span(:class="$style.markerChosen", v-if="activeDirectory && !showActiveDirectoryTooltip" @mouseover="mouseOver" ref="activeDirectory") {{ activeDirectory.name }}
            div(:class="$style.answerTemplatesHeaderWrap")
              div(:class="$style.subTitle") {{ `${$t('T_ANSWER_TEMPLATES')}:` }}
              div(:class="$style.searchWrap")
                v-text-field(:disabled="searchDisabled" v-model.trim="templateSearchText", hide-details
                prepend-inner-icon="search", :class="$style.onSearchTemplates" @keydown.enter.prevent="onSearchTemplates()" clearable)
        div(:class="$style.searchWrapMini" v-if="miniMode")
          div(v-if="isSearchMode" :class="$style.searchModeIndicatorWrap")
            div(:class="$style.searchModeIndicator") {{ $t('T_SEARCH_MODE') }}
          v-text-field(:disabled="searchDisabled" v-model.trim="templateSearchText", hide-details
          prepend-inner-icon="search", :class="$style.onSearchTemplates" @keydown.enter.prevent="onSearchTemplates()" clearable)
        div(:class="[$style.formBodyContentWrap, miniMode && $style.formBodyContentWrapMini]")
          ui-progress-circle(v-if="isSearchLoading")
          div(v-if="!isSearchLoading" :class="[$style.formBodyContent, miniMode && $style.formBodyContentMini]")
            div(:class="[$style.columnContent, miniMode && $style.columnContentMini]")
              div(v-if="miniMode" :class="[$style.subTitle, $style.subTitleMini]") {{ $t('T_ANSWER_TEMPLATE_THEMES') }}
              div(:class="[$style.actions, miniMode && $style.actionsMini]" v-if="!selectedTemplate")
                div(:class="[$style.noDataText, miniMode && $style.noDataTextMini, miniMode && $style.italic]"
                    v-if="sortedDirectories.length === 0 || !hasTemplatesFlag") {{ $t('T_NO_THEMES') }}
                div(v-for="(directory, index) in sortedDirectories" :class="$style.flexWrap" @click="onClickDirectoryBtnWrap(directory)" @keyup.enter="onClickDirectoryBtnWrap(directory)")
                  ui-button(v-if="hasAnswerTemplates(directory.id)",
                    data-test="THEME",
                    :class="[$style.actionsShape, $style.directory]",
                    :colored="handleDirectoryColor(directory)",
                    :disabled="activeDirectory && directory.id==activeDirectory.id",
                    :size="buttonSize",
                    :key="directory.id",
                    @click.native="onClickDirectoryBtn($event, directory)",
                    @keyup.enter.native="onClickDirectoryBtn($event, directory)")
                    span(:class="$style.text") {{directory.name}}
              div(:class="[$style.actions, miniMode && $style.actionsMini]" v-if="selectedTemplate")
                div(v-for="(directory, index) in selectedTemplateDirectories" :class="$style.flexWrap")
                  ui-button(data-test="THEME",
                    :class="[$style.actionsShape, $style.directory]",
                    :colored="handleDirectoryColor(directory)",
                    :disabled="directory.id==selectedTemplateActiveDirectory.id",
                    :size="buttonSize",
                    :key="directory.id",
                    @click.native="onClickSelectedTemplateDirectoryBtn(directory)",
                    @keyup.enter.native="onClickSelectedTemplateDirectoryBtn(directory)")
                    span(:class="$style.text") {{directory.name}}
            div(:class="[$style.columnContent, miniMode && $style.columnContentMini, miniMode && $style.marginTop]")
              div(v-if="miniMode" :class="$style.subTitleMini" v-html="handleMarkersForDirectoryMini")
              div(v-if="!selectedTemplate")
                div(:class="[$style.noDataText, miniMode && $style.noDataTextMini, miniMode && $style.italic]"
                v-if="markers.length === 0 && !loadingMarks || !hasMarkerTemplatesFlag") {{ $t('T_NO_MARKERS') }}
                div(:class="[$style.actions, miniMode && $style.actionsMini]" v-if="!loadingMarks")
                  div(:class="$style.flexWrap")
                    ui-button(data-test="MARKER",
                      v-for="(marker, index) in markers",
                      :class="[$style.actionsShape, $style.mark]",
                      colored="primary",
                      :size="buttonSize",
                      :key="marker.id",
                      :disabled="disabledMarker(marker.id)",
                      @submit="setActiveMarker(marker)",
                      @keyup.enter.native="setActiveMarker(marker)",
                      v-if="!loadingMarks && hasMarkerTemplates(marker.id)")
                      span(:class="$style.text") {{marker.name}}
                ui-progress-circle(v-if="loadingMarks")
              div(v-if="selectedTemplate")
                div(:class="[$style.noDataText, miniMode && $style.noDataTextMini, miniMode && $style.italic]"
                v-if="selectedTemplateMarkersByDirectory.length === 0") {{ $t('T_NO_MARKERS') }}
                div(:class="[$style.actions, miniMode && $style.actionsMini]")
                  div(:class="$style.flexWrap")
                    ui-button(data-test="MARKER",
                      v-for="(marker, index) in selectedTemplateMarkersByDirectory",
                      :class="[$style.actionsShape, $style.mark]",
                      colored="primary",
                      :size="buttonSize",
                      :key="marker.id",
                      :disabled="disabledMarker(marker.id)",
                      @submit="setActiveMarker(marker)",
                      @keyup.enter.native="setActiveMarker(marker)",
                      v-if="!loadingMarks")
                      span(:class="$style.text") {{marker.name}}
            div(:class="[$style.wrapTemplates, miniMode && $style.wrapTemplatesMini]")
              div(:class="$style.templates", v-if="value.isClosed")
                div(:class="[$style.noDataText, miniMode && $style.noDataTextMini]") {{ $t('T_ACTIVITY_IS_CLOSED_IMPOSSIBLE_TO_ADD_ANSWER_TEMPLATE') }}
              div(:class="$style.templates", v-if="templatesInView.length === 0 && !loadingMarks && !value.isClosed")
                div(:class="[$style.noDataText, miniMode && $style.noDataTextMini]" v-html="handleNoTemplatesText")
              div(:class="$style.templates" v-if="!loadingMarks && !value.isClosed")
                div(:class="[$style.template, isSelectedTemplate(template) && $style.templateSelected, miniMode && $style.templateMini]", :key="template.id", v-for="(template, index) in templatesInView" @click="clickTemplate(template)")
                  div(:class="[$style.description, miniMode && $style.descriptionMini]", data-test="TEXT_TEMPLATE_LIST" disabled v-html="handleTemplateVal(template.value)")
                  div(:class="[$style.action, miniMode && $style.actionMini]")
                    ui-button(:class="[$style.actionsShape, $style.send]",
                      colored="primary",
                      :size="buttonSize",
                      @click.native="setTemplate($event, template.value)",
                      @keyup.enter.native="setTemplate($event, template.value)"
                      data-test="REPLY_BUTTON")
                      span(:class="$style.text" v-if="template.value") {{ $t('T_ANSWER') }}
  ui-progress(:class="$style.loading", v-if="loadingFormData")
</template>

<style lang="sass">
.menu__content
  z-index: 10009

</style>

<style lang="sass" module>
.templates
  display: flex
  flex-direction: column
.template
  display: flex
  align-items: center
  margin-bottom: 20px
  background: #FFFFFF
  padding: 20px 0 20px 30px
  border-radius: 10px
  &Mini
    flex-direction: column
    padding: 20px 20px 0
  &Selected
    background: $color_bg_light_grey
  .description
    padding: 0
    color: $color_text
    font-weight: 400
    font-size: 16px
    text-overflow: ellipsis
    word-break: break-word
    white-space: pre-wrap
    flex: 4
    &Mini
      font-size: 14px
    &.disabled
      cursor: default
      opacity: 0.7
.template:last-of-type
  margin-bottom: 0
.action
  display: flex
  margin: 0 8px 0 24px
  justify-content: space-around
  &Mini
    margin: 20px 0
.form
  display: block
  margin: 25px 48px 40px
  width: 1200px
  &Mini
    margin: 36px 40px 20px
    width: 250px

.formHeader
  display: flex
  flex-grow: 1
  justify-content: center
  align-items: center
  font-size: 18px
  font-weight: 400
  padding-bottom: 2px
  margin-bottom: 10px
  margin-left: 12px

.formBody
  display: flex
  flex-direction: column

.formBodyHeaders
  display: flex
  margin-top: 10px
  height: 50px

.formBodyContent
  display: flex
  margin: 10px 0
  &Mini
    flex-direction: column
    align-items: center
  &Wrap
    height: 500px
    position: relative
    &Mini
      height: auto
.title
  display: flex
  justify-content: center
  align-items: center
  font-size: 18px
  font-weight: 400
  padding-bottom: 2px
  margin-left: 12px
  flex-grow: 1
  &Mini
    font-size: 16px
    margin-left: 0
    justify-content: space-between
.subTitle
  display: flex
  justify-content: flex-start
  font-size: 18px
  font-weight: 400
  text-overflow: ellipsis
  overflow: hidden
  white-space: nowrap
  &Mini
    font-size: 16px
    height: auto
    display: block
    margin-bottom: 10px
    text-align: center

.center
  justify-content: center

.markers
  margin: 0 8px
  display: flex
  justify-content: center
  flex-wrap: wrap
.content
  display: flex
  flex-direction: column
  min-width: 484px
  margin-bottom: 16px
  opacity: 1
  transition: height $a_hide_slow, opacity $a_speed
  .text
    color: $white
.actions
  overflow: auto
  height: 500px
  width: 200px
  &Mini
    height: auto
  &Shape
    padding: 0 8px
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis
    display: flex
    justify-content: center
    align-items: center
    cursor: pointer
    margin-right: 8px
    margin-bottom: 8px
    &.directory
      border-radius: 16px
      margin-right: 0
    &.mark
      border-radius: 2px
      margin-right: 0
.send
  border-radius: 2px
  display: flex
  flex-grow: 1
  flex-shrink: 0
  margin: 0 20px
.icon
  width: 36px
  height: 36px
  display: flex
  align-items: center
  justify-content: center
  border-radius: 50%
  cursor: pointer
  &:hover
    background: $color_main_light_third
    color: $color_msg_dark
  &:focus
    background: $color_main_light_third
    color: $color_msg_dark
    outline: none
  .close
    width: 70%
.notification
  display: flex
  justify-content: center
  color: $color_red
  font-size: 12px
  &Hidden
    opacity: 0
.loading
  position: fixed
  left: 0
  right: 0
  bottom: 0

.noMarkers
  display: flex
  flex-direction: column
  align-items: center

.columnHeader
  width: 200px
  margin-right: 40px

.columnContent
  width: 200px
  margin-right: 40px
  height: 500px
  &Mini
    margin-right: 0
    width: 100%
    height: auto

.markersColumnHeader
  font-weight: 400
  font-size: 18px

.markerChosen
  display: inline-block
  text-overflow: ellipsis
  overflow: hidden
  white-space: nowrap
  max-width: 190px
  color: $color_text
.flexWrap
  display: flex
  flex-direction: column
.wrapTemplates
  overflow: auto
  height: 500px
  width: 58%
  &Mini
    height: auto
    width: 100%
    margin-top: 30px

.answerTemplatesHeaderWrap
  width: 58%
  display: flex
  justify-content: space-between
.searchWrap
  width: 200px
  margin-top: -20px
  &Mini
    margin-top: auto
    margin-bottom: 30px

.noDataText
  font-size: 16px
  margin-top: 5px
  &Mini
    text-align: center
    margin-top: 0
.italic
  font-style: italic

.table
  padding: 10px 20px 10px
  &Search
    margin-top: 14px
    border-radius: 16px
    border: 1px solid $color_social_vk
    -webkit-box-shadow: 0px 0px 8px 0px rgba(34, 60, 80, 0.2)
    -moz-box-shadow: 0px 0px 8px 0px rgba(34, 60, 80, 0.2)
    box-shadow: 0px 0px 8px 0px rgba(34, 60, 80, 0.2)

.searchModeIndicator
  padding: 0 10px
  background: $color_social_vk
  color: white
  border-radius: 16px
  &Wrap
    display: flex
    justify-content: flex-end
.marginTop
  margin-top: 20px
</style>
